import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useEffect, useRef, useState } from 'react';
/** kaneai AUTOPLAY_INTERVAL_MS. */
var INTERVAL_MS = 1200;
var IDLE = {
  active: false,
  currentKey: null,
  revealed: new Set()
};

/**
 * Lean port of kaneai's autoplay: every 1.2s the "executing" step (spinner
 * dot) reveals to its real status and the cursor advances — selection and
 * the screenshot panel follow. Unplayed steps render as grey
 * 'autoplay_pending' dots with their duration chips hidden. Stops at the
 * end (selection resets to the first step), ON a failed step (stays there),
 * or on any manual interaction.
 */
export function useAutoplay(_ref) {
  var flatSteps = _ref.flatSteps,
    selectedKey = _ref.selectedKey,
    onSelect = _ref.onSelect,
    scrollRef = _ref.scrollRef;
  var _useState = useState(IDLE),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  var stateRef = useRef(state);
  stateRef.current = state;
  var stepsRef = useRef(flatSteps);
  stepsRef.current = flatSteps;
  var onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  var centerScroll = useCallback(function (key) {
    var container = scrollRef.current;
    var el = container === null || container === void 0 ? void 0 : container.querySelector("[data-step-key=\"".concat(CSS.escape(key), "\"]"));
    if (!container || !el) return;
    var elRect = el.getBoundingClientRect();
    var containerRect = container.getBoundingClientRect();
    var offset = elRect.top - containerRect.top - containerRect.height / 2 + elRect.height / 2;
    container.scrollBy({
      top: offset,
      behavior: 'smooth'
    });
  }, [scrollRef]);
  var stop = useCallback(function () {
    setState(IDLE);
  }, []);
  var start = useCallback(function () {
    var steps = stepsRef.current;
    if (steps.length === 0) return;
    var startIdx = steps.findIndex(function (s) {
      return s.key === selectedKey;
    });
    if (startIdx < 0 || startIdx >= steps.length - 1) startIdx = 0;
    var revealed = new Set();
    for (var i = 0; i < startIdx; i += 1) revealed.add(steps[i].key);
    var key = steps[startIdx].key;
    setState({
      active: true,
      currentKey: key,
      revealed: revealed
    });
    onSelectRef.current(key);
    centerScroll(key);
  }, [selectedKey, centerScroll]);
  var toggle = useCallback(function () {
    if (stateRef.current.active) stop();else start();
  }, [start, stop]);
  useEffect(function () {
    if (!state.active) return undefined;
    var timer = setInterval(function () {
      var prev = stateRef.current;
      var steps = stepsRef.current;
      if (!prev.active || prev.currentKey === null) return;
      var idx = steps.findIndex(function (s) {
        return s.key === prev.currentKey;
      });
      var revealed = new Set(prev.revealed);
      if (idx >= 0) revealed.add(steps[idx].key);

      // Advance past skipped steps (kaneai skips skipped/queued instantly).
      var next = idx + 1;
      while (next < steps.length && steps[next].status === 'skipped') {
        revealed.add(steps[next].key);
        next += 1;
      }
      if (next >= steps.length) {
        // Completed: reveal everything, reset selection to the first step.
        setState(IDLE);
        var first = steps[0];
        if (first) {
          onSelectRef.current(first.key);
          centerScroll(first.key);
        }
        return;
      }
      var nextStep = steps[next];
      onSelectRef.current(nextStep.key);
      centerScroll(nextStep.key);
      if (nextStep.status === 'failed') {
        // Stop ON the failed step and stay there, fully revealed.
        revealed.add(nextStep.key);
        setState(IDLE);
        return;
      }
      setState({
        active: true,
        currentKey: nextStep.key,
        revealed: revealed
      });
    }, INTERVAL_MS);
    return function () {
      return clearInterval(timer);
    };
  }, [state.active, centerScroll]);

  // A different pack/test unmounts the page; still, drop state if steps change.
  useEffect(function () {
    setState(IDLE);
  }, [flatSteps]);
  var displayStatus = useCallback(function (key, real) {
    var s = stateRef.current;
    if (!s.active) return real;
    if (s.revealed.has(key)) return real;
    if (s.currentKey === key) return 'executing';
    return 'autoplay_pending';
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [state]);
  return {
    active: state.active,
    displayStatus: displayStatus,
    toggle: toggle,
    stop: stop
  };
}